import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Drop-in replacement for react-full-screen built on the native Fullscreen
// API. Keeps the same `fullscreen`/`fullscreen-enabled` classes and inline
// sizing the session styles rely on. webkit-prefixed fallbacks cover Safari
// versions that predate the unprefixed API (< 16.4).
const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;

const requestFullscreen = element => {
  const request = element.requestFullscreen || element.webkitRequestFullscreen;
  if (request) {
    const result = request.call(element);
    if (result && result.catch) {
      result.catch(() => {});
    }
  }
};

const exitFullscreen = () => {
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  if (exit) {
    const result = exit.call(document);
    if (result && result.catch) {
      result.catch(() => {});
    }
  }
};

const Fullscreen = ({ enabled = false, onChange = () => {}, children }) => {
  const nodeRef = useRef(null);
  const wasFullscreenRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const detectFullscreen = () => {
      if (getFullscreenElement() === nodeRef.current) {
        wasFullscreenRef.current = true;
        onChangeRef.current(true);
      } else if (!getFullscreenElement() && wasFullscreenRef.current) {
        wasFullscreenRef.current = false;
        onChangeRef.current(false);
      }
    };

    document.addEventListener('fullscreenchange', detectFullscreen);
    document.addEventListener('webkitfullscreenchange', detectFullscreen);
    return () => {
      document.removeEventListener('fullscreenchange', detectFullscreen);
      document.removeEventListener('webkitfullscreenchange', detectFullscreen);
    };
  }, []);

  useEffect(() => {
    const isFullscreen = getFullscreenElement() === nodeRef.current;
    if (enabled && !isFullscreen) {
      requestFullscreen(nodeRef.current);
    } else if (!enabled && isFullscreen) {
      exitFullscreen();
    }
  }, [enabled]);

  return (
    <div
      className={enabled ? 'fullscreen fullscreen-enabled' : 'fullscreen'}
      ref={nodeRef}
      style={enabled ? { height: '100%', width: '100%' } : undefined}
    >
      {children}
    </div>
  );
};

Fullscreen.propTypes = {
  children: PropTypes.node.isRequired,
  enabled: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Fullscreen;
